import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkle, UploadSimple, Play, FloppyDisk, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import CustomGiftPreview from "../components/CustomGiftPreview";
import { playGiftSound } from "../lib/giftSounds";

const EMOJIS = ["🎁", "👑", "🔥", "❤️", "💃", "🕺", "🇯🇲", "🌴", "🎉", "⭐", "💎", "🏆", "🎶", "🥁", "🌊", "☀️", "🦁", "🕊️", "✨", "🎤", "🍹", "🎭", "🏝️", "🙌"];

/** GiftCreator — design a custom animated gift, add artwork (upload or AI), save draft, submit for review. */
export default function GiftCreator() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");
  const [tpl, setTpl] = useState(null);
  const [gift, setGift] = useState({ name: "", text: "", emoji: "👑", description: "", animation: "pop", sfx: "cheer", palette: "jamaica", category: "celebration", price_waves: 100, image_path: null, image_url: null });
  const [saved, setSaved] = useState(null); // saved server doc
  const [busy, setBusy] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [agree, setAgree] = useState(false);
  const [replay, setReplay] = useState(0);
  const [wallet, setWallet] = useState(null);
  const fileRef = useRef(null);
  const fee = Number(tpl?.pricing?.creation_fee_waves || 0);
  const canAfford = wallet === null || fee === 0 || (wallet.waves || 0) >= fee;

  const palette = tpl?.palettes?.find((p) => p.id === gift.palette) || tpl?.palettes?.[0];
  const previewGift = { ...gift, colors: palette ? { bg1: palette.bg1, bg2: palette.bg2, text: palette.text } : undefined };

  const load = useCallback(async () => {
    try {
      const { data: t } = await api.get("/gifts/templates");
      setTpl(t);
      api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
      if (!editId) setGift((g) => ({ ...g, price_waves: t.pricing.default_price_waves }));
      if (editId) {
        const { data: g } = await api.get(`/gifts/custom/${editId}`);
        setSaved(g);
        setGift({ name: g.name, text: g.text || "", emoji: g.emoji, description: g.description || "", animation: g.animation, sfx: g.sfx, palette: g.palette, category: g.category, price_waves: g.price_waves, image_path: g.image_path, image_url: g.image_url });
      }
    } catch (e) { toast.error("Could not load gift designer"); }
  }, [editId]);

  useEffect(() => { load(); }, [load]);

  const body = () => ({ name: gift.name, text: gift.text, emoji: gift.emoji, description: gift.description, animation: gift.animation, sfx: gift.sfx, palette: gift.palette, category: gift.category, price_waves: Number(gift.price_waves), image_path: gift.image_path });

  const save = async (silent = false) => {
    if (gift.name.trim().length < 2) { toast.error("Give your gift a name (2+ characters)"); return null; }
    setBusy("save");
    try {
      const { data } = saved ? await api.put(`/gifts/custom/${saved.id}`, body()) : await api.post("/gifts/custom", body());
      setSaved(data);
      if (!silent) toast.success("Draft saved");
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); return null; }
    finally { setBusy(""); }
  };

  const submit = async () => {
    if (!agree) return toast.error("Please accept the Gift Creator Agreement");
    const d = await save(true);
    if (!d) return;
    setBusy("submit");
    try {
      await api.post(`/gifts/custom/${d.id}/submit`, { agree_terms: true });
      toast.success("Submitted! Our team will review your gift.");
      nav("/gifts?tab=mine");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submit failed";
      toast.error(msg);
      if (e?.response?.status === 422) nav("/gifts?tab=mine");
      if (e?.response?.status === 402) nav("/shop");
    } finally { setBusy(""); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("art");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/gifts/artwork/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGift((g) => ({ ...g, image_path: data.image_path, image_url: data.image_url }));
      setReplay((r) => r + 1);
    } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  const generateArt = async () => {
    if (aiPrompt.trim().length < 4) return toast.error("Describe your gift artwork");
    setBusy("ai");
    try {
      const { data } = await api.post("/gifts/artwork/ai", { prompt: aiPrompt });
      setGift((g) => ({ ...g, image_path: data.image_path, image_url: data.image_url }));
      setReplay((r) => r + 1);
      toast.success("AI artwork ready — tweak the prompt to regenerate");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI artwork failed"); }
    finally { setBusy(""); }
  };

  const locked = saved && !["draft", "rejected"].includes(saved.status);

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="gift-creator">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/gifts")} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="giftcreator-back"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Gift Creator</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">{saved ? "Edit your gift" : "Create a Gift"}</h1>
        </div>
      </div>
      {locked && <div className="mt-4 rounded-2xl bg-papaya/10 border border-papaya/30 p-3 text-papaya text-sm">This gift is {saved.status.replace("_", " ")} and can no longer be edited.</div>}

      {/* Live preview */}
      <div className="mt-6 flex flex-col items-center">
        <div className="relative">
          <CustomGiftPreview gift={previewGift} size={240} replayKey={replay} />
          <button onClick={() => { setReplay((r) => r + 1); if (gift.sfx !== "none") playGiftSound(gift.sfx); }} data-testid="giftcreator-replay" className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1 shadow-lg"><Play size={12} weight="fill" /> Play</button>
        </div>
      </div>

      <fieldset disabled={!!locked} className="mt-8 space-y-5 disabled:opacity-60">
        <Field label="Gift name">
          <input value={gift.name} onChange={(e) => setGift({ ...gift, name: e.target.value.slice(0, 40) })} placeholder="King of Dance" data-testid="giftcreator-name" className={inputCls} />
        </Field>
        <Field label="Text on gift (optional)">
          <input value={gift.text} onChange={(e) => setGift({ ...gift, text: e.target.value.slice(0, 40) })} placeholder="BIG UP!" data-testid="giftcreator-text" className={inputCls} />
        </Field>
        <Field label="Symbol">
          <div className="flex gap-1.5 flex-wrap" data-testid="giftcreator-emojis">
            {EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setGift({ ...gift, emoji: e })} data-testid={`giftcreator-emoji-${e}`} className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center border ${gift.emoji === e ? "bg-papaya/20 border-papaya" : "bg-white/5 border-white/10"}`}>{e}</button>
            ))}
            <input value={gift.emoji} onChange={(e) => setGift({ ...gift, emoji: e.target.value.slice(0, 8) })} className="w-16 h-10 rounded-xl bg-white/5 border border-white/10 text-center text-cream text-lg" title="Custom emoji" />
          </div>
        </Field>
        <Field label="Artwork (optional) — upload or generate with AI">
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="giftcreator-upload" className="flex-1 py-3 rounded-2xl bg-white/5 border border-white/10 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5"><UploadSimple size={14} weight="bold" /> {busy === "art" ? "Uploading…" : "Upload image"}</button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="giftcreator-file" />
            {gift.image_path && <button type="button" onClick={() => setGift({ ...gift, image_path: null, image_url: null })} data-testid="giftcreator-clear-art" className="w-11 rounded-2xl bg-red-500/15 text-red-300 flex items-center justify-center"><X size={16} /></button>}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value.slice(0, 400))} placeholder="Golden crown with Caribbean colors and the words KING OF DANCE" data-testid="giftcreator-ai-prompt" className={inputCls} />
            <button type="button" onClick={generateArt} disabled={busy !== ""} data-testid="giftcreator-ai-generate" className="px-4 rounded-2xl bg-jam-gold text-jam-black text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1 disabled:opacity-50 flex-shrink-0"><Sparkle size={14} weight="fill" /> {busy === "ai" ? "…" : "AI"}</button>
          </div>
          <p className="text-cream/40 text-[10px] mt-1">No copyrighted characters, logos, brands or real people.</p>
        </Field>
        <Field label="Animation">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="giftcreator-animations">
            {(tpl?.animations || []).map((a) => (
              <button key={a.id} type="button" onClick={() => { setGift({ ...gift, animation: a.id }); setReplay((r) => r + 1); }} data-testid={`giftcreator-anim-${a.id}`} title={a.desc} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${gift.animation === a.id ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>{a.label}</button>
            ))}
          </div>
        </Field>
        <Field label="Colors">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="giftcreator-palettes">
            {(tpl?.palettes || []).map((p) => (
              <button key={p.id} type="button" onClick={() => setGift({ ...gift, palette: p.id })} data-testid={`giftcreator-palette-${p.id}`} className={`flex-shrink-0 w-12 h-12 rounded-2xl border-2 ${gift.palette === p.id ? "border-cream" : "border-transparent"}`} style={{ background: `linear-gradient(135deg, ${p.bg1}, ${p.bg2})` }} title={p.id} />
            ))}
          </div>
        </Field>
        <Field label="Sound effect">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="giftcreator-sfx">
            {(tpl?.sfx || []).map((s) => (
              <button key={s} type="button" onClick={() => { setGift({ ...gift, sfx: s }); if (s !== "none") playGiftSound(s); }} data-testid={`giftcreator-sfx-${s}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${gift.sfx === s ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>{s.replace("_", " ")}</button>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select value={gift.category} onChange={(e) => setGift({ ...gift, category: e.target.value })} data-testid="giftcreator-category" className={inputCls}>
              {(tpl?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={`Price (${tpl?.pricing?.min_price_waves ?? 20}–${tpl?.pricing?.max_price_waves ?? 5000} waves)`}>
            <input type="number" min={tpl?.pricing?.min_price_waves} max={tpl?.pricing?.max_price_waves} value={gift.price_waves} onChange={(e) => setGift({ ...gift, price_waves: e.target.value })} data-testid="giftcreator-price" className={inputCls} />
          </Field>
        </div>
        <Field label="Description (for reviewers)">
          <textarea rows={2} value={gift.description} onChange={(e) => setGift({ ...gift, description: e.target.value.slice(0, 300) })} placeholder="What is this gift about?" data-testid="giftcreator-description" className={inputCls} />
        </Field>
      </fieldset>

      {!locked && (
        <div className="mt-8 rounded-3xl crystal-glass border border-white/10 p-4" id="submit">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="giftcreator-agree" className="mt-1 w-4 h-4 accent-[#C66A4A]" />
            <span className="text-cream/80 text-xs leading-relaxed">
              I confirm this design is my original work (or I hold the rights) and I accept the <Link to="/gift-creator-agreement" className="text-papaya underline" data-testid="giftcreator-agreement-link">Gift Creator Agreement</Link>: if approved, the gift becomes part of the official Giffwurldaunce catalog under the licence described there.
            </span>
          </label>
          {fee > 0 && (
            <div className={`mt-4 rounded-2xl p-3 border ${canAfford ? "bg-jam-gold/10 border-jam-gold/30" : "bg-red-500/10 border-red-500/30"}`} data-testid="giftcreator-fee">
              <p className={`text-[10px] tracking-[0.25em] uppercase font-black ${canAfford ? "text-jam-gold" : "text-red-300"}`}>Submission fee · {fee} waves</p>
              <p className="text-cream/70 text-xs mt-0.5">
                Charged when you submit, non-refundable — even if the gift is rejected. {wallet && <>Your balance: <b className="text-cream" data-testid="giftcreator-balance">{wallet.waves} 🪙</b>.</>}
                {!canAfford && <> <Link to="/shop" className="text-papaya underline" data-testid="giftcreator-topup">Top up in the Coin Shop</Link>.</>}
              </p>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={() => save(false)} disabled={busy !== ""} data-testid="giftcreator-save" className="flex-1 py-3.5 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><FloppyDisk size={14} weight="bold" /> {busy === "save" ? "Saving…" : "Save draft"}</button>
            <button onClick={submit} disabled={busy !== "" || !agree || !canAfford} data-testid="giftcreator-submit" className="flex-1 py-3.5 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><PaperPlaneTilt size={14} weight="fill" /> {busy === "submit" ? "Submitting…" : fee > 0 ? `Submit · ${fee} 🪙` : "Submit for review"}</button>
          </div>
          <p className="text-cream/40 text-[11px] mt-3">{tpl?.creator_reward_pct > 0 ? `Designer reward currently ${tpl.creator_reward_pct}% of each sale (set by Giffwurldaunce, may change).` : "When your gift is sent to a host, the host earns the host share and Giffwurldaunce keeps the platform share. No designer royalty is paid unless a reward program is shown here."}</p>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-cream/70 text-[10px] tracking-[0.2em] uppercase font-bold block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
